/**
 * Reglas de anomalía de caja para el strip de Finanzas (#14 → #13).
 *
 * Producto: el dueño puede preguntar “¿por qué dice atención?” y el dev
 * responde con la fila de esta tabla (umbrales exactos, sin magia en el JSX).
 *
 * | Señal                         | Umbral              | Nivel    | Mensaje UI (clave i18n)        |
 * |-------------------------------|---------------------|----------|--------------------------------|
 * | Δ ingresos vs mes ant.        | ≤ −25%              | critical | anomalyReason.revenue_critical |
 * | Δ neto vs mes ant.            | ≤ −25%              | critical | anomalyReason.net_critical     |
 * | Δ ingresos vs mes ant.        | ≤ −15%              | watch    | anomalyReason.revenue_drop     |
 * | Δ neto vs mes ant.            | ≤ −15%              | watch    | anomalyReason.net_drop         |
 * | Top medio: share vs mes ant.  | ≥ +25 pp            | watch    | anomalyReason.payment_mix      |
 * | Sin base / prev 0 / mes vacío | —                   | none     | anomalyReason.none             |
 *
 * Resolución: se toma el **peor** nivel; `primaryReason` es la primera razón
 * de esa severidad (crítico > atención).
 */

import type { CajaReporteFormaPago } from './metricsTypes';

/** Δ % MoM (ingresos o neto) que dispara Atención / “baja fuerte”. */
export const FINANCE_ANOMALY_DROP_WATCH_PCT = -15;
/** Δ % MoM que dispara Crítico. */
export const FINANCE_ANOMALY_DROP_CRITICAL_PCT = -25;
/**
 * Suba del share del top medio vs el mismo medio el mes anterior (puntos porcentuales)
 * que dispara Atención.
 */
export const FINANCE_ANOMALY_MIX_SHARE_SPIKE_PP = 25;

export type FinanceAnomalyLevel = 'none' | 'watch' | 'critical';

export type FinanceAnomalyReasonCode =
  | 'revenue_critical'
  | 'net_critical'
  | 'revenue_drop'
  | 'net_drop'
  | 'payment_mix'
  | 'none';

export interface FinanceAnomalyCashSide {
  ingresos: number;
  neto: number;
  movimientos: number;
  porFormaPago?: CajaReporteFormaPago[];
}

export interface FinanceAnomalyInput {
  current: FinanceAnomalyCashSide;
  previous: FinanceAnomalyCashSide | null;
  /** Mix solo tiene sentido con desglose de clínica (no red agregada). */
  evaluatePaymentMix?: boolean;
}

export interface FinanceAnomalyResult {
  level: FinanceAnomalyLevel;
  reasons: FinanceAnomalyReasonCode[];
  primaryReason: FinanceAnomalyReasonCode;
  /** Δ % ingresos MoM; null si no comparable. */
  revenueDeltaPct: number | null;
  /** Δ % neto MoM; null si no comparable. */
  netDeltaPct: number | null;
  /** Datos de mix si disparó payment_mix. */
  mix?: {
    methodLabel: string | null;
    sharePct: number;
    shareDeltaPp: number;
  };
}

function relativeChangePct(current: number, previous: number): number | null {
  if (previous === 0) {
    return null;
  }
  return ((current - previous) / previous) * 100;
}

function methodKey(formaPago: string | null): string {
  return formaPago ?? '__sin_forma_pago__';
}

function sharePctForMethod(
  porFormaPago: CajaReporteFormaPago[] | undefined,
  label: string | null,
  ingresosTotal: number
): number | null {
  if (!porFormaPago?.length || ingresosTotal <= 0) {
    return null;
  }
  const key = methodKey(label);
  const row = porFormaPago.find((f) => methodKey(f.formaPago) === key);
  if (!row || row.ingresos <= 0) {
    return null;
  }
  return (row.ingresos / ingresosTotal) * 100;
}

function pickTopRow(
  porFormaPago: CajaReporteFormaPago[] | undefined
): CajaReporteFormaPago | null {
  if (!porFormaPago?.length) {
    return null;
  }
  const top = [...porFormaPago].sort((a, b) => b.ingresos - a.ingresos)[0];
  return top && top.ingresos > 0 ? top : null;
}

const LEVEL_RANK: Record<FinanceAnomalyLevel, number> = {
  none: 0,
  watch: 1,
  critical: 2,
};

/**
 * Evalúa anomalías de caja. Mes vacío o sin periodo anterior comparable → `none`
 * (no inventa −100% ni NaN).
 */
export function resolveFinanceAnomaly(input: FinanceAnomalyInput): FinanceAnomalyResult {
  const { current, previous, evaluatePaymentMix = false } = input;
  const empty = current.movimientos === 0 && current.ingresos === 0 && current.neto === 0;

  if (empty || previous === null) {
    return {
      level: 'none',
      reasons: ['none'],
      primaryReason: 'none',
      revenueDeltaPct: null,
      netDeltaPct: null,
    };
  }

  const revenueDeltaPct = relativeChangePct(current.ingresos, previous.ingresos);
  const netDeltaPct = relativeChangePct(current.neto, previous.neto);

  const reasons: FinanceAnomalyReasonCode[] = [];
  let level: FinanceAnomalyLevel = 'none';
  let mix: FinanceAnomalyResult['mix'];

  const bump = (next: FinanceAnomalyLevel, reason: FinanceAnomalyReasonCode) => {
    if (LEVEL_RANK[next] > LEVEL_RANK[level]) {
      level = next;
    }
    if (!reasons.includes(reason)) {
      reasons.push(reason);
    }
  };

  if (revenueDeltaPct !== null && revenueDeltaPct <= FINANCE_ANOMALY_DROP_CRITICAL_PCT) {
    bump('critical', 'revenue_critical');
  } else if (revenueDeltaPct !== null && revenueDeltaPct <= FINANCE_ANOMALY_DROP_WATCH_PCT) {
    bump('watch', 'revenue_drop');
  }

  if (netDeltaPct !== null && netDeltaPct <= FINANCE_ANOMALY_DROP_CRITICAL_PCT) {
    bump('critical', 'net_critical');
  } else if (netDeltaPct !== null && netDeltaPct <= FINANCE_ANOMALY_DROP_WATCH_PCT) {
    bump('watch', 'net_drop');
  }

  if (evaluatePaymentMix && current.ingresos > 0 && previous.ingresos > 0) {
    const top = pickTopRow(current.porFormaPago);
    if (top) {
      const currentShare = (top.ingresos / current.ingresos) * 100;
      const prevShare = sharePctForMethod(previous.porFormaPago, top.formaPago, previous.ingresos);
      if (prevShare !== null) {
        const shareDeltaPp = currentShare - prevShare;
        if (shareDeltaPp >= FINANCE_ANOMALY_MIX_SHARE_SPIKE_PP) {
          bump('watch', 'payment_mix');
          mix = {
            methodLabel: top.formaPago,
            sharePct: currentShare,
            shareDeltaPp,
          };
        }
      }
    }
  }

  if (level === 'none' || reasons.length === 0) {
    return {
      level: 'none',
      reasons: ['none'],
      primaryReason: 'none',
      revenueDeltaPct,
      netDeltaPct,
    };
  }

  const criticalFirst = reasons.find((r) => r.endsWith('_critical'));
  const primaryReason =
    criticalFirst ??
    reasons.find((r) => r !== 'payment_mix') ??
    reasons[0] ??
    'none';

  return {
    level,
    reasons,
    primaryReason,
    revenueDeltaPct,
    netDeltaPct,
    mix,
  };
}
